import React, { useState } from 'react'
import { format } from 'date-fns'
import { useMatchScreen } from '../../../context/MatchScreenContext'
import { trackOptimizelyEvent } from '../../../utils/tracking/optimizely'
import { trackEvent } from '../../../utils/tracking/mixpanel'
import SelfScheduleButton from '../../components/SelfScheduleButton/SelfScheduleButton'
import SelfScheduleModal from '../../components/SelfScheduleModal/SelfScheduleModal'
import BookedConfirmation from '../../components/BookedConfirmation/BookedConfirmation'
import { useUserTimezone } from '../../hooks/useUserTimezone'
import { useSelfSchedulingEligibility } from '../../hooks/useSelfSchedulingEligibility'
import { AdvisorPhotoProfile } from './AdvisorPhotoProfile'
import { useAdvisorCard } from './AdvisorCardContext'
import { submitSelfScheduleBooking } from './selfScheduleBooking'
import '../../styles/AdvisorCardsModule/ScheduleCallSection.scss'

export const ScheduleCallSection = () => {
  const { advisorData } = useAdvisorCard()
  const advisor = advisorData.advisor
  const { state: { sessionUUID, matches } } = useMatchScreen()
  const { abbreviation, timeZone } = useUserTimezone()
  const isSelfSchedulingEligible = useSelfSchedulingEligibility()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [booking, setBooking] = useState<{ date: Date, time: string } | null>(null)

  if (!isSelfSchedulingEligible) {
    return null
  }

  // Mirror useSendSeenAdv: the lead/user session id is the numeric `sessionId` carried on the
  // matches (sent stringified), not the analytics sessionUUID.
  const leadUserSessionId = matches?.find((match) => match?.sessionId != null)?.sessionId

  const handleSelfScheduleClick = () => {
    const eventPayload = {
      advisor_name: advisor.name,
      session_uuid: sessionUUID
    }

    trackOptimizelyEvent('funnel-match-screen-self-schedule-cta-clicked', eventPayload)
    trackEvent('MatchScreen', 'funnel-match-screen-self-schedule-cta-clicked', eventPayload)

    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
  }

  const handleAppointmentConfirm = async (date: Date, time: string) => {
    // Propagates on failure so the modal can surface the error and stay open;
    // tracking and the booked state only run once the request succeeds.
    await submitSelfScheduleBooking({
      leadUserSessionId: String(leadUserSessionId),
      advisorData,
      date,
      time,
      timezone: timeZone
    })

    const eventName = 'funnel-match-screen-self-schedule-booked'
    const trackingEventPayload = {
      advisor_name: advisor.name,
      session_uuid: sessionUUID,
      date_selected: format(date, 'EEE, MMM d, yyyy'),
      time_selected: time,
      timezone: abbreviation
    }
    trackOptimizelyEvent(eventName, trackingEventPayload)
    trackEvent('MatchScreen', eventName, trackingEventPayload)

    setBooking({ date, time })
  }

  return (
    <section className="schedule-call-section">
      {booking
        ? <BookedConfirmation date={booking.date} time={booking.time} />
        : <SelfScheduleButton onClick={handleSelfScheduleClick} />}
      <SelfScheduleModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        advisorProfile={<AdvisorPhotoProfile />}
        onConfirm={handleAppointmentConfirm}
      />
    </section>
  )
}
